<template>
  <section class="flex h-full flex-col gap-3">
    <div class="rounded-lg border border-slate-200 bg-white p-3">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="min-w-0">
          <h3 class="text-sm font-semibold text-slate-900">链路树</h3>
          <p class="mt-0.5 text-xs text-slate-500">
            {{ plan?.planMonth || '-' }} · {{ chains.length }} 条链路 · {{ nonPlatformTotal }} 个非平台主体
            <span v-if="errorCount > 0" class="ml-2 text-red-600">· {{ errorCount }} 个 ERROR</span>
            <span v-if="productNameWarningCount > 0" class="ml-2 text-amber-600">· {{ productNameWarningCount }} 个品名未匹配</span>
          </p>
        </div>
        <div class="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
          <!-- <button
            type="button"
            class="btn-secondary inline-flex items-center gap-1.5"
            :disabled="syncing"
            @click="emit('validate')"
          >
            <ShieldCheckIcon class="h-4 w-4" />
            校验方案
          </button> -->
          <button
            type="button"
            class="btn-secondary inline-flex items-center gap-1.5"
            v-permission="'enterprise-invoice:chain:export'"
            :disabled="syncing || exporting || chains.length === 0"
            @click="emit('export-chains')"
          >
            <ArrowDownTrayIcon class="h-4 w-4" />
            {{ exporting ? '导出中...' : '导出 Excel' }}
          </button>
          <button
            type="button"
            class="btn-secondary inline-flex items-center gap-1.5"
            v-permission="'enterprise-invoice:chain:generate'"
            :disabled="syncing"
            @click="emit('regenerate-unlocked')"
          >
            <ArrowPathIcon class="h-4 w-4" />
            重排未锁定
          </button>
          <button
            type="button"
            class="btn-secondary inline-flex items-center gap-1.5"
            v-permission="'enterprise-invoice:chain:generate'"
            :disabled="syncing"
            @click="emit('generate-unfilled')"
          >
            <PlusCircleIcon class="h-4 w-4" />
            补齐未生成
          </button>
          <button
            type="button"
            class="btn-primary inline-flex items-center gap-1.5"
            v-permission="'enterprise-invoice:chain:generate'"
            :disabled="syncing || !canGenerate"
            :title="canGenerate ? '生成链路' : '请先确认名单后再生成链路'"
            @click="emit('generate')"
          >
            <BoltIcon class="h-4 w-4" />
            生成链路
          </button>
        </div>
      </div>
    </div>

    <div v-if="chains.length > 0" class="rounded-lg border border-slate-200 bg-white p-3">
      <div class="flex flex-wrap items-end gap-3">
        <div class="min-w-0 w-full sm:min-w-[260px] flex-1">
          <label class="mb-1 block text-xs font-medium text-slate-600">公司上下游</label>
          <input
            :value="companyRelationKeyword"
            type="search"
            class="form-search-input form-control-sm w-full"
            placeholder="输入公司名称查询直接上下游"
            @input="event => emit('update:companyRelationKeyword', event.target.value)"
            @keyup.enter="submitCompanyRelationSearch"
          >
        </div>
        <button
          type="button"
          class="btn-secondary inline-flex w-full items-center justify-center gap-1.5 sm:w-auto"
          :disabled="syncing || companyRelationLoading"
          @click="submitCompanyRelationSearch"
        >
          <MagnifyingGlassIcon class="h-4 w-4" />
          {{ companyRelationLoading ? '搜索中...' : '搜索' }}
        </button>
        <button
          v-if="companyRelationResult"
          type="button"
          class="btn-secondary w-full sm:w-auto"
          :disabled="companyRelationLoading"
          @click="clearCompanyRelationSearch"
        >
          清空
        </button>
      </div>

      <div v-if="companyRelationResult" class="mt-3 border-t border-slate-100 pt-3">
        <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p class="text-xs text-slate-500">
            “{{ companyRelationResult.companyName || companyRelationKeyword }}” 命中
            <span class="font-semibold text-slate-900">{{ companyRelationMatches.length }}</span>
            条链路，每条展示直接上游、自身、直接下游
          </p>
        </div>
        <div v-if="companyRelationMatches.length === 0" class="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-sm text-slate-500">
          未查询到相关链路
        </div>
        <div v-else class="grid gap-2 xl:grid-cols-2">
          <article
            v-for="(match, matchIndex) in companyRelationMatches"
            :key="companyRelationMatchKey(match, matchIndex)"
            class="rounded-lg border border-slate-200 bg-slate-50 p-2.5"
          >
            <header class="mb-2 flex items-center justify-between gap-2">
              <div class="min-w-0">
                <span class="text-sm font-semibold text-slate-900">链路 {{ companyRelationChainNo(match, matchIndex) }}</span>
                <span class="ml-2 text-xs text-slate-500">{{ companyRelationNodeCount(match) }} 个节点</span>
              </div>
              <button
                type="button"
                class="table-action-button is-primary"
                title="定位链路" aria-label="定位链路"
                @click="emit('locate-company-relation', match)"
              >
                <MapPinIcon class="h-4 w-4" />
              </button>
            </header>
            <div class="grid gap-2 md:grid-cols-3">
              <div
                v-for="item in companyRelationSegments(match)"
                :key="item.role"
                :class="[
                  'rounded-md border bg-white p-2',
                  item.role === 'self' ? 'border-blue-200 ring-1 ring-blue-100' : 'border-slate-200'
                ]"
              >
                <div class="mb-1 flex items-center justify-between gap-2">
                  <span :class="['status-badge', item.badgeClass]">{{ item.label }}</span>
                  <span v-if="item.edge" class="text-xs font-medium text-slate-500">{{ formatInvoiceAmount(item.edge.invoiceAmount) }}</span>
                </div>
                <p class="truncate text-sm font-semibold text-slate-900" :title="companyRelationNodeName(item.node)">
                  {{ companyRelationNodeName(item.node) }}
                </p>
                <p class="mt-1 truncate text-xs text-slate-500">
                  {{ item.edge?.productName || item.node?.productName || '-' }}
                </p>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>

    <div v-if="chains.length === 0" class="data-table-empty bg-white">
      <p class="text-sm font-medium text-slate-700">暂无链路</p>
      <p class="mt-1 text-xs text-slate-500">先确认名单后再生成链路树</p>
      <button
        type="button"
        class="btn-primary mt-3 inline-flex items-center gap-1.5"
        v-permission="'enterprise-invoice:chain:generate'"
        :disabled="syncing || !canGenerate"
        @click="emit('generate')"
      >
        <BoltIcon class="h-4 w-4" />
        生成链路
      </button>
    </div>

    <div v-else ref="treeScrollRef" class="flex-1 min-h-0 max-w-full space-y-2 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
      <article
        v-for="(chain, chainIndex) in chains"
        :key="chain.id || chain.chainNo || chainIndex"
        :data-chain-id="chain.id"
        :class="[
          'w-max min-w-full rounded-lg border bg-white p-2.5',
          isFocusChain(chain) ? 'border-amber-300 ring-2 ring-amber-100' : 'border-slate-200'
        ]"
      >
        <header class="mb-2 flex flex-wrap items-center gap-2">
          <div class="invoice-chain-actions-left flex shrink-0 items-center gap-1.5">
            <span class="mr-1 text-sm font-semibold text-slate-900">链路 {{ chain.chainNo || chainIndex + 1 }}</span>
            <button
              type="button"
              class="table-action-button is-purple"
              v-permission="'enterprise-invoice:chain:generate'"
              title="重排本链" aria-label="重排本链"
              :disabled="syncing || chain.manualLocked || chainHasLockedNode(chain) || chainHasSpecified(chain)"
              @click="emit('regenerate-chain', chain)"
            >
              <ArrowPathIcon class="h-4 w-4" />
            </button>
            <button
              type="button"
              :class="['table-action-button', chain.manualLocked ? 'is-success' : 'is-neutral']"
              v-permission="'enterprise-invoice:chain:lock'"
              :title="chain.manualLocked ? '解锁本链' : '锁定本链'" :aria-label="chain.manualLocked ? '解锁本链' : '锁定本链'"
              :disabled="syncing"
              @click="emit('lock-chain', { chain, locked: !chain.manualLocked })"
            >
              <LockClosedIcon v-if="!chain.manualLocked" class="h-4 w-4" />
              <LockOpenIcon v-else class="h-4 w-4" />
            </button>
          </div>
          <div class="flex flex-wrap items-baseline gap-2">
            <span class="text-xs text-slate-500">{{ nonPlatformCount(chain) }} 个主体</span>
            <span v-if="chainHasSpecified(chain)" class="status-badge bg-emerald-50 text-emerald-700">含指定下游</span>
          </div>
        </header>

        <div class="invoice-chain-tree-root flex w-max flex-col gap-2">
          <div
            v-for="root in treeOf(chain)"
            :key="root.node?.id || root.node?.planCustomerId"
          >
            <TreeNode
              :entry="root"
              :chain="chain"
              :regions="regions"
              :issue-mark-map="issueMarkMap"
              :syncing="syncing"
              :has-specified-edge="chainHasSpecified(chain)"
              @edit-node="payload => emit('edit-node', payload)"
              @lock-node="payload => emit('lock-node', payload)"
              @regenerate-after-node="payload => emit('regenerate-after-node', payload)"
            />
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
import { computed, defineComponent, h, nextTick, watch } from 'vue'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  BoltIcon,
  LockClosedIcon,
  LockOpenIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PlusCircleIcon
} from '@heroicons/vue/24/outline'
import {
  canGenerateChains,
  formatInvoiceAmount,
  hasLockedNodeField,
  issueMarkByKey,
  issueMarkFor,
  isPlatformNode,
  nonPlatformSubjects,
  regionNameById,
  sortChainNodes
} from '../utils'
import {
  buildChainTree,
  chainHasUserSpecifiedEdge,
  edgeSourceLabel,
  edgeSourceTone
} from '../downstreamRules.mjs'
import InvoiceChainMatrixCell from './InvoiceChainMatrixCell.vue'
import { useWheelToHorizontalScroll } from '../composables/useWheelToHorizontalScroll'

const props = defineProps({
  plan: { type: Object, default: null },
  chains: { type: Array, default: () => [] },
  customers: { type: Array, default: () => [] },
  regions: { type: Array, default: () => [] },
  validationIssues: { type: Array, default: () => [] },
  syncing: { type: Boolean, default: false },
  exporting: { type: Boolean, default: false },
  focusChainId: { type: [Number, String, null], default: null },
  companyRelationKeyword: { type: String, default: '' },
  companyRelationResult: { type: Object, default: null },
  companyRelationLoading: { type: Boolean, default: false }
})

const emit = defineEmits([
  'update:companyRelationKeyword',
  'generate',
  'export-chains',
  'regenerate-unlocked',
  'generate-unfilled',
  'edit-node',
  'lock-chain',
  'lock-node',
  'regenerate-chain',
  'regenerate-after-node',
  'search-company-relation',
  'clear-company-relation',
  'locate-company-relation',
  'validate'
])

const treeScrollRef = useWheelToHorizontalScroll()
const canGenerate = computed(() => canGenerateChains(props.plan))
const issueMarkMap = computed(() => issueMarkByKey(props.validationIssues))

const nonPlatformTotal = computed(() =>
  props.chains.reduce((total, chain) => total + nonPlatformSubjects(chain).length, 0)
)
const errorCount = computed(
  () => props.validationIssues.filter(issue => String(issue?.severity || '').toUpperCase() === 'ERROR').length
)
const productNameWarningCount = computed(
  () => props.validationIssues.filter(issue => issue?.ruleCode === 'R_PRODUCT_NAME_NOT_MATCHED').length
)
const companyRelationMatches = computed(() => props.companyRelationResult?.matches || [])

const TreeNode = defineComponent({
  name: 'InvoiceChainTreeNode',
  props: {
    entry: { type: Object, required: true },
    chain: { type: Object, required: true },
    regions: { type: Array, default: () => [] },
    issueMarkMap: { type: Object, default: null },
    syncing: { type: Boolean, default: false },
    hasSpecifiedEdge: { type: Boolean, default: false },
    depth: { type: Number, default: 0 }
  },
  emits: ['edit-node', 'lock-node', 'regenerate-after-node'],
  setup(componentProps, { emit: componentEmit }) {
    const regionMap = computed(() => regionNameById(componentProps.regions))

    function nodeRegionName(node) {
      if (!node || node.regionBucketId == null) return ''
      return regionMap.value.get(String(node.regionBucketId)) || ''
    }

    function edgeItems(edge) {
      if (!edge) return []
      return edge.productItems || edge.edgeProductItems || []
    }

    function edgeProductName(edge) {
      return edge?.productName || ''
    }

    function edgeProductMatched(edge) {
      if (!edge) return true
      return edge.productNameMatched === false ? false : true
    }

    function canRerankAfter(node) {
      if (!node || isPlatformNode(node) || componentProps.hasSpecifiedEdge) return false
      const subjects = nonPlatformSubjects(componentProps.chain)
      if (subjects.length === 0) return false
      const last = subjects[subjects.length - 1]
      return last.id !== node.id
    }

    function onToggleLock(node, fieldKey) {
      const locks = {
        companyLocked: Boolean(node.companyLocked),
        inputAmountLocked: Boolean(node.inputAmountLocked),
        outputAmountLocked: Boolean(node.outputAmountLocked),
        profitRateLocked: Boolean(node.profitRateLocked),
        productNameLocked: Boolean(node.productNameLocked)
      }
      locks[fieldKey] = !locks[fieldKey]
      componentEmit('lock-node', { chain: componentProps.chain, node, locks })
    }

    return () => {
      const entry = componentProps.entry
      const node = entry.node
      const incomingEdge = entry.incomingEdge
      const edgeSource = incomingEdge?.edgeSource || 'AUTO'
      const sourceTone = edgeSourceTone(edgeSource)
      const badgeClass = {
        emerald: 'bg-emerald-50 text-emerald-700',
        indigo: 'bg-indigo-50 text-indigo-700',
        slate: 'bg-slate-100 text-slate-600'
      }[sourceTone] || 'bg-slate-100 text-slate-600'

      const edgeSummary = incomingEdge
        ? h('div', { class: 'mb-0.5 flex min-h-[20px] flex-wrap items-center gap-1.5 text-xs text-slate-500' }, [
              h('span', { class: ['status-badge', badgeClass] }, edgeSourceLabel(edgeSource)),
              h('span', { class: 'font-medium text-slate-700' }, formatInvoiceAmount(incomingEdge.invoiceAmount)),
              incomingEdge.productName
                ? h('span', { class: incomingEdge.productNameMatched === false ? 'text-amber-700' : 'text-slate-500' }, incomingEdge.productName)
                : null
            ])
        : null

      const nodeCard = h('div', { class: 'w-[248px] shrink-0' }, [
        edgeSummary,
          h(InvoiceChainMatrixCell, {
            node,
            regionName: nodeRegionName(node),
            issueMark: issueMarkFor(componentProps.issueMarkMap, componentProps.chain.id, node),
            edgeProductName: edgeProductName(incomingEdge),
            edgeProductMatched: edgeProductMatched(incomingEdge),
            edgeProductItems: edgeItems(incomingEdge),
            syncing: componentProps.syncing,
            isPlatform: isPlatformNode(node),
            canRerankAfter: canRerankAfter(node),
            onEdit: () => componentEmit('edit-node', { chain: componentProps.chain, node }),
            onToggleLock: fieldKey => onToggleLock(node, fieldKey),
            onRegenerateAfter: () => componentEmit('regenerate-after-node', { chain: componentProps.chain, node })
          })
      ])

      const childStack = entry.children?.length
        ? h('div', { class: 'flex flex-col gap-2' }, entry.children.map(child =>
            h(TreeNode, {
              key: child.node?.id || child.node?.planCustomerId,
              entry: child,
              chain: componentProps.chain,
              regions: componentProps.regions,
              issueMarkMap: componentProps.issueMarkMap,
              syncing: componentProps.syncing,
              hasSpecifiedEdge: componentProps.hasSpecifiedEdge,
              depth: componentProps.depth + 1,
              onEditNode: payload => componentEmit('edit-node', payload),
              onLockNode: payload => componentEmit('lock-node', payload),
              onRegenerateAfterNode: payload => componentEmit('regenerate-after-node', payload)
            })
          ))
        : null

      return h('div', { class: 'invoice-chain-tree-node inline-flex items-start gap-1.5' }, [
        nodeCard,
        childStack
          ? h('div', { class: 'invoice-chain-connector mt-14 flex w-6 shrink-0 justify-center text-sm leading-none text-slate-300' }, '→')
          : null,
        childStack
      ])
    }
  }
})

function chainNodes(chain) {
  return sortChainNodes(chain?.nodes || chain?.chainNodes || [])
}

function nonPlatformCount(chain) {
  return nonPlatformSubjects(chain).length
}

function treeOf(chain) {
  return buildChainTree(chain)
}

function chainHasSpecified(chain) {
  return chainHasUserSpecifiedEdge(chain)
}

function chainHasLockedNode(chain) {
  return chainNodes(chain).some(node => hasLockedNodeField(node))
}

function isFocusChain(chain) {
  return props.focusChainId != null && String(chain.id) === String(props.focusChainId)
}

function submitCompanyRelationSearch() {
  emit('search-company-relation', props.companyRelationKeyword)
}

function clearCompanyRelationSearch() {
  emit('clear-company-relation')
}

function companyRelationMatchKey(match, index) {
  return [
    match?.chain?.id,
    match?.targetNode?.id,
    match?.targetNode?.planCustomerId,
    index
  ].filter(value => value !== null && value !== undefined && value !== '').join(':')
}

function companyRelationChainNo(match, index) {
  return match?.chain?.chainNo || index + 1
}

function companyRelationNodeCount(match) {
  return (match?.nodes || companyRelationSegments(match).map(item => item.node).filter(Boolean)).length
}

function companyRelationSegments(match) {
  return [
    {
      role: 'upstream',
      label: '上游',
      node: match?.upstreamNode || null,
      edge: match?.upstreamEdge || null,
      badgeClass: 'bg-slate-100 text-slate-600'
    },
    {
      role: 'self',
      label: '自身',
      node: match?.targetNode || null,
      edge: null,
      badgeClass: 'bg-blue-100 text-blue-700'
    },
    {
      role: 'downstream',
      label: '下游',
      node: match?.downstreamNode || null,
      edge: match?.downstreamEdge || null,
      badgeClass: 'bg-emerald-50 text-emerald-700'
    }
  ]
}

function companyRelationNodeName(node) {
  if (!node) return '-'
  return node.effectiveCompanyName || node.manualCompanyName || node.systemCompanyName || node.companyNameSnapshot || node.companyName || '-'
}

watch(
  () => props.focusChainId,
  (id) => {
    if (id == null) return
    nextTick(() => {
      const el = document.querySelector(`article[data-chain-id="${id}"]`)
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
    })
  }
)
</script>
